import { useMutation } from '@tanstack/react-query';

import axios from 'axios';
import { MutationConfig, queryClient } from 'lib/react-query';

import { SnapshotHistorySetting } from 'types';
import { CreateUpdateSnapshotHistorySetting } from '../types';
import { convertSnapshotHistorySettings } from 'api/dtoConverters';

export const createSnapshotHistorySettings = ({
  data,
  snapshotId,
}: {
  data: CreateUpdateSnapshotHistorySetting;
  snapshotId: number;
}): Promise<SnapshotHistorySetting> => {
  return axios.post(
    `/api/snapshots/${snapshotId}/history/settings`,
    convertSnapshotHistorySettings(data)
  );
};

type UseCreateSnapshotHistorySettingsOptions = {
  config?: MutationConfig<typeof createSnapshotHistorySettings>;
};

export const useCreateSnapshotHistorySettings = ({
  config,
}: UseCreateSnapshotHistorySettingsOptions) => {
  return useMutation({
    onMutate: async (newSnapshotHistorySettings) => {
      await queryClient.cancelQueries([
        'snapshot',
        'historySettings',
        newSnapshotHistorySettings.snapshotId,
      ]);

      const previousSnapshotHistorySettings =
        queryClient.getQueryData<SnapshotHistorySetting>([
          'snapshot',
          'historySettings',
          newSnapshotHistorySettings.snapshotId,
        ]);

      queryClient.setQueryData(
        ['snapshot', 'historySettings', newSnapshotHistorySettings.snapshotId],
        newSnapshotHistorySettings.data
      );

      return previousSnapshotHistorySettings;
    },
    onError: (_, __, context: any) => {
      if (context?.previousSnapshotHistorySettings) {
        const snapshotId = context.previousSnapshotHistorySettings?.snapshot_id
          ? context.previousSnapshotHistorySettings.snapshot_id
          : context.previousSnapshotHistorySettings.snapshotId;
        queryClient.setQueryData(
          ['snapshot', 'historySettings', snapshotId],
          context.previousSnapshotHistorySettings
        );
      }
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries([
        'snapshot',
        'historySettings',
        response.snapshot_id,
      ]);
    },
    ...config,
    mutationFn: createSnapshotHistorySettings,
  });
};
